import { useEffect, useMemo, useState } from "react";
import { compileForm } from "@bjalon/form-runtime/compiler";
import type { Unsubscribe } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { subscribeSessionPublicQuestionnaires } from "../services/accessProjectionService";
import {
  listQuestionnaireAnswersForSession,
  updateQuestionnaireRuntimeStatus,
} from "../services/questionnaireRuntimeService";
import type {
  QuestionnaireAnswerRecord,
  SessionPublicQuestionnaire,
} from "../types/access";
import type { QuestionnaireRuntimeStatus } from "../types/session";

const statusLabels: Record<QuestionnaireRuntimeStatus, string> = {
  closed: "Clôturé",
  closed_to_new_entries: "Fermé aux nouveaux accès",
  correction_visible: "Correction visible",
  not_opened: "Non ouvert",
  open: "Ouvert",
};

function questionCount(questionnaire: SessionPublicQuestionnaire): number {
  if (!questionnaire.yamlDefinition) {
    return 0;
  }

  const result = compileForm(
    {
      content: questionnaire.yamlDefinition,
      uri: `qorelia-session://${questionnaire.sessionId}/${questionnaire.id}.yaml`,
    },
    undefined,
    { mode: "strict" },
  );

  if (result.status === "invalid") {
    return 0;
  }

  return result.form.pages.reduce(
    (total, page) =>
      total +
      page.elements.filter((element) => element.type === "question").length,
    0,
  );
}

function answerStats(
  answers: QuestionnaireAnswerRecord[],
  questionnaireId: string,
) {
  const relatedAnswers = answers.filter(
    (answer) => answer.questionnaireRuntimeId === questionnaireId,
  );
  const submitted = relatedAnswers.filter(
    (answer) => answer.status === "submitted",
  ).length;
  const drafts = relatedAnswers.length - submitted;

  return {
    drafts,
    submitted,
    total: relatedAnswers.length,
  };
}

export function SessionQuestionnaireRuntimePanel({
  sessionId,
}: {
  sessionId: string | null;
}) {
  const { user } = useAuth();
  const [answers, setAnswers] = useState<QuestionnaireAnswerRecord[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [questionnaires, setQuestionnaires] = useState<
    SessionPublicQuestionnaire[]
  >([]);

  const openQuestionnaires = useMemo(
    () =>
      questionnaires.filter(
        (questionnaire) =>
          questionnaire.runtimeStatus === "open" ||
          questionnaire.runtimeStatus === "closed_to_new_entries",
      ),
    [questionnaires],
  );

  useEffect(() => {
    if (!sessionId) {
      setAnswers([]);
      setQuestionnaires([]);
      return undefined;
    }

    const unsubscribe: Unsubscribe = subscribeSessionPublicQuestionnaires({
      onError: (error) => {
        console.error(error);
        setMessage("Impossible de suivre les questionnaires live.");
      },
      onNext: setQuestionnaires,
      sessionId,
    });

    void listQuestionnaireAnswersForSession(sessionId)
      .then(setAnswers)
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger le suivi questionnaire.");
      });

    return () => unsubscribe();
  }, [sessionId]);

  async function handleTransition({
    questionnaire,
    transition,
  }: {
    questionnaire: SessionPublicQuestionnaire;
    transition:
      | "open"
      | "close_to_new_entries"
      | "close"
      | "publish_correction";
  }) {
    if (!user || !sessionId) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      await updateQuestionnaireRuntimeStatus({
        actorUid: user.uid,
        questionnaire,
        sessionId,
        transition,
      });
      setAnswers(await listQuestionnaireAnswersForSession(sessionId));
      setMessage("Statut questionnaire mis à jour.");
    } catch (error: unknown) {
      console.error(error);
      setMessage("Impossible de modifier ce questionnaire.");
    } finally {
      setIsSaving(false);
    }
  }

  async function refreshAnswers() {
    if (!sessionId) {
      return;
    }

    try {
      setAnswers(await listQuestionnaireAnswersForSession(sessionId));
    } catch (error: unknown) {
      console.error(error);
      setMessage("Impossible de rafraîchir le suivi questionnaire.");
    }
  }

  if (!sessionId) {
    return null;
  }

  return (
    <section className="maintenance-block">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Questionnaires live</p>
          <h3>Ouverture et suivi</h3>
        </div>
        <button
          className="button button-secondary button-small"
          onClick={() => void refreshAnswers()}
          type="button"
        >
          Rafraîchir
        </button>
      </div>

      {openQuestionnaires.length > 0 ? (
        <div className="alert alert-warning" role="status">
          {openQuestionnaires.length} questionnaire(s) encore ouvert(s).
        </div>
      ) : null}

      {questionnaires.length > 0 ? (
        <ul className="admin-list">
          {questionnaires.map((questionnaire) => {
            const stats = answerStats(answers, questionnaire.id);
            return (
              <li key={questionnaire.id}>
                <div>
                  <strong>{questionnaire.title}</strong>
                  <span>
                    {statusLabels[questionnaire.runtimeStatus]} -{" "}
                    {questionCount(questionnaire)} question(s) - {stats.submitted}{" "}
                    soumise(s), {stats.drafts} brouillon(s)
                  </span>
                </div>
                <div className="row-actions">
                  <button
                    className="button button-secondary button-small"
                    disabled={isSaving || questionnaire.runtimeStatus !== "not_opened"}
                    onClick={() =>
                      void handleTransition({
                        questionnaire,
                        transition: "open",
                      })
                    }
                    type="button"
                  >
                    Ouvrir
                  </button>
                  <button
                    className="button button-secondary button-small"
                    disabled={isSaving || questionnaire.runtimeStatus !== "open"}
                    onClick={() =>
                      void handleTransition({
                        questionnaire,
                        transition: "close_to_new_entries",
                      })
                    }
                    type="button"
                  >
                    Fermer entrées
                  </button>
                  <button
                    className="button button-secondary button-small"
                    disabled={
                      isSaving ||
                      !(
                        questionnaire.runtimeStatus === "open" ||
                        questionnaire.runtimeStatus === "closed_to_new_entries"
                      )
                    }
                    onClick={() =>
                      void handleTransition({
                        questionnaire,
                        transition: "close",
                      })
                    }
                    type="button"
                  >
                    Clôturer
                  </button>
                  <button
                    className="button button-primary button-small"
                    disabled={isSaving || questionnaire.runtimeStatus !== "closed"}
                    onClick={() =>
                      void handleTransition({
                        questionnaire,
                        transition: "publish_correction",
                      })
                    }
                    type="button"
                  >
                    Correction
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p>Aucun questionnaire publié dans cette session.</p>
      )}

      {message ? (
        <div className="alert alert-info" role="status">
          {message}
        </div>
      ) : null}
    </section>
  );
}
